import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { StatsContainer } from './StatCard';
import HotAgents from './HotAgents';
import {
  fallbackAgents,
  fallbackPrograms,
  fallbackHotAgents,
} from './fallbackData';
import HubSearchResults from './HubSearchResults';
import {
  loadStatsApi,
  loadAgentsApi,
  loadProgramsApi,
  loadHotAgentsApi,
} from './../../../api';
import './HubPreview.scss';

// Icons remain the same...
const AgentsIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
  </svg>
);

const ProgramsIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0L19.2 12l-4.6-4.6L16 6l6 6-6 6-1.4-1.4z" />
  </svg>
);

const ActiveUsersIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const FlowsIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM16 18H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
  </svg>
);

const HubPreview = () => {
  const [stats, setStats] = useState([]);
  const [agents, setAgents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [hotAgents, setHotAgents] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsData, agentsData, programsData, hotAgentsData] =
          await Promise.all([
            loadStatsApi().catch(() => [
              {
                icon: <AgentsIcon />,
                value: '12,847',
                label: 'Agents',
                color: '#3B82F6',
              },
              {
                icon: <ProgramsIcon />,
                value: '3,256',
                label: 'Programs',
                color: '#10B981',
              },
              {
                icon: <ActiveUsersIcon />,
                value: '45,123',
                label: 'Active Users',
                color: '#F59E0B',
              },
              {
                icon: <FlowsIcon />,
                value: '8,934',
                label: 'Flows',
                color: '#8B5CF6',
              },
            ]),
            loadAgentsApi('').catch(() => fallbackAgents),
            loadProgramsApi('').catch(() => fallbackPrograms),
            loadHotAgentsApi().catch(() => fallbackHotAgents),
          ]);

        setStats(statsData);
        setAgents(agentsData.results || []);
        setPrograms(programsData.results || []);
        setHotAgents(hotAgentsData || []);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  console.log('Rendering HubPreview with data:');

  return (
    <section className="hub-preview-section py-5" id="hub-preview">
      <div className="container">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Section Header */}
          <div className="row mb-5">
            <div className="col-lg-8 mx-auto text-center">
              <motion.div variants={itemVariants}>
                <h1 className="display-4 fw-bold mb-4">
                  Discover & Share{' '}
                  <span className="text-success">AI Agents</span>
                </h1>
                <p className="lead text-muted mb-4">
                  Join a thriving ecosystem of developers building and sharing
                  AI agents. Find pre-built solutions or contribute your own to
                  the community.
                </p>
              </motion.div>
            </div>
          </div>

          {/* Hub Stats */}
          <StatsContainer stats={stats} />

          {/* Hot Agents Section */}
          <HotAgents agents={hotAgents} />

          <motion.div variants={itemVariants}>
            <HubSearchResults
              initialAgents={agents}
              initialPrograms={programs}
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HubPreview;
